import type {SketchPointObject} from '../../../mesh/sketch/SketchPointObject';
import type {SketchGroup} from '../../../mesh/sketch/SketchGroup';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import type {SketchReferenceTargetEndpointCanonicalization} from '../target/SketchReferenceTargetEndpointCanonicalization';
import type {
	SketchEntityReferenceRebuildBatchTransactionInput,
	SketchEntityReferenceRebuildBatchTransactionState,
	SketchEntityReferenceBeforeImageRestoration,
	SketchEntityReferenceCommittedRemoval,
	SketchEntityReferenceRebuildMutation,
	SketchEntityReferenceRebuildTransactionPort,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildBatchTransaction';

/** checkpoint 所有权失效时用于穿透内部调用栈的唯一中止信号。 */
export const STALE_SKETCH_ENTITY_REFERENCE_REBUILD = Symbol(
	'stale-sketch-entity-reference-rebuild',
);

/**
 * 转换实体引用 Rebuild 的运行时补偿事务。
 *
 * repository 负责持久记录的原子提交；本类只记录同一轮计算已经落地到草图对象上的可逆变更，
 * 并把旧实例释放等不可逆动作延迟到 repository 提交成功之后。
 */
export class SketchEntityReferenceRebuildBatchTransaction implements SketchEntityReferenceRebuildTransactionPort {
	/** 当前事务状态。 */
	private state: SketchEntityReferenceRebuildBatchTransactionState = 'open';

	/** 当前批次已经应用的端点拓扑统一候选。 */
	private endpointCanonicalization: SketchReferenceTargetEndpointCanonicalization | null = null;

	/** 当前端点恢复调用明确创建、允许本事务撤销的关系 ID。 */
	private readonly restoredRelationIds = new Set<number>();

	/** 尚未回滚、等待最终确认的运行时变更。 */
	private mutations: SketchEntityReferenceRebuildMutation[] = [];

	/** 正式提交后待按目标草图合并发布的真实实体删除。 */
	private committedRemovals: SketchEntityReferenceCommittedRemoval[] = [];

	/** @param input 可选的端点拓扑事务输入。 */
	public constructor(private readonly input?: SketchEntityReferenceRebuildBatchTransactionInput) {}

	/**
	 * 在一次同步基础关系恢复调用周围捕获本次新增的确切关系 ID。
	 *
	 * 同步调用期间不会让出事件循环，因此差集只属于当前恢复动作；后续用户关系不进入
	 * 本事务所有权，stale 回滚时也不会被误删。
	 *
	 * @param work 基础关系恢复调用。
	 * @returns work 的返回值。
	 */
	public captureRestoredRelations<T>(work: () => T): T {
		this.ensureOpen();
		const relationIdsBefore = new Set(
			this.collectTargetPoints().flatMap(targetPoint => (
				targetPoint.constraints.getRelations().map(relation => relation.id)
			)),
		);
		try {
			return work();
		} finally {
			this.collectTargetPoints().forEach((targetPoint) => {
				targetPoint.constraints.getRelations().forEach((relation) => {
					if (!relationIdsBefore.has(relation.id)) {
						this.restoredRelationIds.add(relation.id);
					}
				});
			});
		}
	}

	/**
	 * 登记一项已落地的运行时变更。
	 *
	 * @param mutation 具备回滚和可选提交后清理语义的变更。
	 */
	public record(mutation: SketchEntityReferenceRebuildMutation): void {
		this.ensureOpen();
		this.mutations.push(mutation);
	}

	/**
	 * 把单条重建的仓储与运行时 before-image 登记为两个独立补偿步骤。
	 *
	 * 仓储步骤先登记、运行时步骤后登记；全局逆序回滚时先恢复运行时，再恢复仓储，
	 * 且事务会分别捕获异常，避免运行时恢复失败截断唯一事实源恢复。
	 *
	 * @param restoration 双重补偿输入。
	 */
	public recordBeforeImageRestoration(
		restoration: SketchEntityReferenceBeforeImageRestoration,
	): void {
		this.record({
			targetSketchId: restoration.targetSketchId,
			batchKey: restoration.batchKey,
			description: `${restoration.description}：仓储记录`,
			rollback: restoration.restoreRepository,
		});
		this.record({
			targetSketchId: restoration.targetSketchId,
			batchKey: restoration.batchKey,
			description: `${restoration.description}：运行时对象`,
			rollback: restoration.restoreRuntime,
			finalize: restoration.finalizeRuntime,
		});
	}

	/** @inheritdoc */
	public recordCommittedEntityRemoval(
		removal: SketchEntityReferenceCommittedRemoval,
	): void {
		this.ensureOpen();
		this.committedRemovals.push({
			...removal,
			removedEntityIds: [...removal.removedEntityIds],
		});
	}

	/**
	 * 只回滚指定原子批次，其他批次继续由当前事务持有。
	 *
	 * @param batchKey 原子批次键。
	 */
	public async rollbackBatch(batchKey: string): Promise<ExecutionDiagnostic[]> {
		return this.rollbackBatchKeys([batchKey]);
	}

	/**
	 * 按全局登记逆序回滚一组等价批次键。
	 *
	 * @param batchKeys 最终批次键及协调阶段语义键。
	 * @returns 回滚过程中产生的补偿诊断。
	 */
	public async rollbackBatchKeys(batchKeys: readonly string[]): Promise<ExecutionDiagnostic[]> {
		this.ensureOpen();
		const batchKeySet = new Set(batchKeys);
		const mutationsToRollback = this.mutations.filter(
			mutation => batchKeySet.has(mutation.batchKey),
		);
		this.mutations = this.mutations.filter(
			mutation => !batchKeySet.has(mutation.batchKey),
		);
		this.committedRemovals = this.committedRemovals.filter(
			removal => !batchKeySet.has(removal.batchKey),
		);
		return this.rollbackMutations(
			mutationsToRollback,
			`批次 ${[...batchKeySet].join(', ')} 回滚失败`,
		);
	}

	/**
	 * 回滚指定目标草图仍由事务持有的全部变更。
	 *
	 * 该入口用于目标提交准备被拒绝时覆盖协调阶段先于最终批次规划登记的派生创建和删除。
	 *
	 * @param targetSketchId 目标草图持久 ID。
	 * @returns 回滚过程中产生的补偿诊断。
	 */
	public async rollbackTargetSketch(targetSketchId: string): Promise<ExecutionDiagnostic[]> {
		this.ensureOpen();
		const mutationsToRollback = this.mutations.filter(
			mutation => mutation.targetSketchId === targetSketchId,
		);
		this.mutations = this.mutations.filter(
			mutation => mutation.targetSketchId !== targetSketchId,
		);
		this.committedRemovals = this.committedRemovals.filter(
			removal => removal.targetSketchId !== targetSketchId,
		);
		return this.rollbackMutations(
			mutationsToRollback,
			`目标草图 ${targetSketchId} 回滚失败`,
		);
	}

	/** 回滚当前事务仍持有的全部运行时变更。 */
	public async rollbackAll(): Promise<ExecutionDiagnostic[]> {
		if (this.state !== 'open') {
			return [];
		}
		const mutationsToRollback = this.mutations;
		this.mutations = [];
		this.committedRemovals = [];
		const errors: unknown[] = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		try {
			diagnostics.push(...await this.rollbackMutations(
				mutationsToRollback,
				'Rebuild 运行时变更回滚失败',
			));
		} catch (error) {
			errors.push(error);
		}
		try {
			this.rollbackRestoredRelations();
			this.endpointCanonicalization?.rollback();
		} catch (error) {
			errors.push(error);
		}
		this.state = 'rolled-back';
		if (errors.length > 0) {
			throw new AggregateError(errors, '转换实体引用 Rebuild 事务回滚失败');
		}
		return diagnostics;
	}

	/**
	 * 在 repository 提交成功后执行不可逆清理。
	 *
	 * 仓储已经提交后不得再抛错；发布监听器或资源清理异常统一转为诊断并继续收尾。
	 *
	 * @returns 提交后清理诊断。
	 */
	public finalize(): ExecutionDiagnostic[] {
		if (this.state !== 'open') {
			return [];
		}
		this.endpointCanonicalization?.commit();
		this.state = 'committed';
		const phaseOrder = {prepare: 0, publish: 1, cleanup: 2} as const;
		const mutationsToFinalize = this.mutations
			.map((mutation, index) => ({mutation, index}))
			.sort((left, right) => (
				phaseOrder[left.mutation.finalizePhase ?? 'cleanup']
				- phaseOrder[right.mutation.finalizePhase ?? 'cleanup']
				|| left.index - right.index
			))
			.map(item => item.mutation);
		this.mutations = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		for (const mutation of mutationsToFinalize) {
			if (!mutation.finalize) {
				continue;
			}
			try {
				const mutationDiagnostics = mutation.finalize();
				if (Array.isArray(mutationDiagnostics)) {
					diagnostics.push(...mutationDiagnostics);
				}
			} catch (error) {
				const description = mutation.description ?? `批次 ${mutation.batchKey} 的提交后清理`;
				diagnostics.push(createExecutionDiagnostic({
					kind: 'compensation-failed',
					level: 'warning',
					message: `${description}失败: ${error instanceof Error ? error.message : String(error)}`,
					targetSketchId: mutation.targetSketchId,
				}));
			}
		}
		const removedEntityIdsByTargetSketch = new Map<SketchGroup, Set<string>>();
		this.committedRemovals.forEach((removal) => {
			const removedEntityIds = removedEntityIdsByTargetSketch.get(removal.targetSketch)
				?? new Set<string>();
			removal.removedEntityIds.forEach(entityId => removedEntityIds.add(entityId));
			removedEntityIdsByTargetSketch.set(removal.targetSketch, removedEntityIds);
		});
		this.committedRemovals = [];
		removedEntityIdsByTargetSketch.forEach((removedEntityIds, targetSketch) => {
			try {
				targetSketch.publishCommittedEntityRemoval([...removedEntityIds]);
			} catch (error) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'compensation-failed',
					level: 'warning',
					message: `发布已提交的草图实体删除事件失败: ${error instanceof Error ? error.message : String(error)}`,
					targetSketchId: targetSketch.persistentId,
				}));
			}
		});
		return diagnostics;
	}

	/**
	 * 应用当前批次端点拓扑统一候选。
	 *
	 * @param preferredTargetPointUuids 应优先保留的既有点 UUID。
	 */
	public applyEndpointCanonicalization(
		preferredTargetPointUuids?: ReadonlySet<string>,
	): void {
		this.ensureOpen();
		if (this.endpointCanonicalization) {
			throw new Error('转换实体引用 Rebuild 批次端点拓扑候选已应用。');
		}
		const input = this.requireEndpointInput();
		this.endpointCanonicalization = input.targetEndpointCanonicalizer.beginCanonicalization(
			input.targetSketch,
			input.targetAnchorBindings,
			preferredTargetPointUuids,
		);
	}

	/** 提交仅使用端点拓扑能力的旧调用链。 */
	public commit(): void {
		if (this.state !== 'open') {
			return;
		}
		this.endpointCanonicalization?.commit();
		this.state = 'committed';
	}

	/** 回滚仅使用端点拓扑能力的旧调用链。 */
	public rollback(): void {
		if (this.state !== 'open') {
			return;
		}
		this.rollbackRestoredRelations();
		this.endpointCanonicalization?.rollback();
		this.state = 'rolled-back';
	}

	/** 撤销事务开始后附加到目标端点的关系。 */
	private rollbackRestoredRelations(): void {
		this.collectTargetPoints().forEach((targetPoint) => {
			targetPoint.constraints.getRelations().forEach((relation) => {
				if (this.restoredRelationIds.has(relation.id)) {
					targetPoint.constraints.removeRelation(relation.id);
				}
			});
		});
		this.restoredRelationIds.clear();
	}

	/**
	 * 按登记逆序撤销一组运行时变更。
	 *
	 * @param mutations 待撤销变更。
	 * @param message 聚合错误消息。
	 */
	private async rollbackMutations(
		mutations: readonly SketchEntityReferenceRebuildMutation[],
		message: string,
	): Promise<ExecutionDiagnostic[]> {
		const diagnostics: ExecutionDiagnostic[] = [];
		for (const mutation of [...mutations].reverse()) {
			try {
				const mutationDiagnostics = await mutation.rollback();
				if (Array.isArray(mutationDiagnostics)) {
					diagnostics.push(...mutationDiagnostics);
				}
			} catch (error) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'compensation-failed',
					level: 'error',
					message: `${message}：${mutation.description ?? mutation.batchKey}：${error instanceof Error ? error.message : String(error)}`,
					targetSketchId: mutation.targetSketchId,
				}));
			}
		}
		return diagnostics;
	}

	/** @returns 当前锚点绑定涉及的全部唯一目标点。 */
	private collectTargetPoints(): SketchPointObject[] {
		if (!this.input) {
			return [];
		}
		return [
			...new Map(
				[...this.input.targetAnchorBindings.values()]
					.flat()
					.map(targetPoint => [targetPoint.uuid, targetPoint]),
			).values(),
		];
	}

	/** @returns 当前端点拓扑事务输入。 */
	private requireEndpointInput(): SketchEntityReferenceRebuildBatchTransactionInput {
		if (!this.input) {
			throw new Error('当前 Rebuild 事务未配置端点拓扑输入。');
		}
		return this.input;
	}

	/** 确保事务仍可接收候选变更。 */
	private ensureOpen(): void {
		if (this.state !== 'open') {
			throw new Error(`转换实体引用 Rebuild 批次事务已关闭: ${this.state}`);
		}
	}
}
